import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  createSurveyAudienceTarget,
  createSurveyAudienceTargetBulk,
  deleteSurveyAudienceTarget,
  fetchSurveyAudienceTarget,
  fetchSurveyAudienceTargetBySurveyId,
  fetchSurveyAudienceTargetQuestion,
  updateSurveyAudienceTarget,
} from 'services/backend/surveyAudienceTarget';
import { ReactQueryFunctionCallbacks } from 'types/internal';

import { surveyQueries } from './surveys';

export const surveyAudienceTargetQueries = {
  all: () => ['survey-audience-targets'],
  get: (opts: { surveyAudienceTargetId: number }) =>
    queryOptions({
      queryFn: () =>
        fetchSurveyAudienceTarget({
          surveyAudienceTargetId: opts.surveyAudienceTargetId,
        }),
      queryKey: [
        ...surveyAudienceTargetQueries.all(),
        opts.surveyAudienceTargetId,
      ],
    }),
  bySurveyId: (opts: { surveyId: number }) =>
    queryOptions({
      queryFn: () =>
        fetchSurveyAudienceTargetBySurveyId({ surveyId: opts.surveyId }),
      queryKey: [...surveyAudienceTargetQueries.all(), opts.surveyId],
    }),
  targetQuestion: (opts: { surveyAudienceTargetId: number }) =>
    queryOptions({
      queryFn: () =>
        fetchSurveyAudienceTargetQuestion({
          surveyAudienceTargetId: opts.surveyAudienceTargetId,
        }),
      queryKey: [
        ...surveyAudienceTargetQueries.all(),
        opts.surveyAudienceTargetId,
        'target-question',
      ],
    }),
};

export function useCreateSurveyAudienceTarget(
  opts?: ReactQueryFunctionCallbacks<typeof createSurveyAudienceTarget>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createSurveyAudienceTarget,
    onSuccess: (...args) => {
      const surveyId = args[1].data.surveyId;

      queryClient.invalidateQueries(surveyQueries.survey({ surveyId }));

      opts?.onSuccess?.(...args);
    },
  });
}

export function useCreateSurveyAudienceTargetBulk(
  opts?: ReactQueryFunctionCallbacks<typeof createSurveyAudienceTargetBulk>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createSurveyAudienceTargetBulk,
    onSuccess: (...args) => {
      const surveyId = args[1].data[0]?.surveyId;

      if (surveyId) {
        queryClient.invalidateQueries(surveyQueries.survey({ surveyId }));
      }

      opts?.onSuccess?.(...args);
    },
  });
}

export function useUpdateSurveyAudienceTarget(
  opts?: ReactQueryFunctionCallbacks<typeof updateSurveyAudienceTarget>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: updateSurveyAudienceTarget,
    onSuccess: (...args) => {
      const surveyId = args[1].data?.surveyId;

      if (surveyId) {
        queryClient.invalidateQueries(surveyQueries.survey({ surveyId }));
      }

      opts?.onSuccess?.(...args);
    },
  });
}

export function useDeleteSurveyAudienceTarget(
  opts?: ReactQueryFunctionCallbacks<typeof deleteSurveyAudienceTarget>,
) {
  return useMutation({ ...opts, mutationFn: deleteSurveyAudienceTarget });
}
